import { createApi } from '@reduxjs/toolkit/query/react';

import { commonParams } from '@/lib/api/baseConfig';
import { createApiErrorTransformer, createBaseQuery } from '@/lib/api/baseQuery';
import type {
  EPledgeDataParams,
  EPledgeDataResponse,
  EPledgeSubmitParams,
  EPledgeSubmitResponse,
} from '@/types/e-pledge';

const E_PLEDGE_API_URL = import.meta.env.VITE_CONNECT_URL;

type EPledgeRawRecord = Record<string, unknown>;

const toNumber = (value: unknown): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

const toString = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return '';
};

const normalizeRow = (row: EPledgeRawRecord): EPledgeDataResponse['data'][number] => ({
  amount: toNumber(row.amount ?? row.AMOUNT ?? row.amt),
  colqty: toNumber(
    row.colqty ?? row.COLQTY ?? row.collateral_qty ?? row.non_poa ?? row.nonPoa ?? row.NON_POA,
  ),
  freeqty: toNumber(
    row.freeqty ?? row.FREEQTY ?? row.free_qty ?? row.poa ?? row.poa_qty ?? row.POA_QTY,
  ),
  haircut_percentage: toNumber(
    row.haircut_percentage ?? row.haircutPercentage ?? row.HAIRCUT_PERCENTAGE ?? row.haircut_pct,
  ),
  haircut_price: toNumber(row.haircut_price ?? row.haircutPrice ?? row.HAIRCUT_PRICE),
  id: toString(row.id ?? row.ID ?? row.sr_no ?? row.SR_NO),
  isin: toString(row.isin ?? row.ISIN),
  net: toNumber(row.net ?? row.NET ?? row.netqty ?? row.net_qty),
  pledgeqty: toNumber(row.pledgeqty ?? row.PLEDGEQTY ?? row.pledge_qty ?? row.pledged_qty),
  price: toNumber(
    row.price ??
      row.PRICE ??
      row.ltp ??
      row.LTP ??
      row.closing_price ??
      row.closingPrice ??
      row.CLOSING_PRICE,
  ),
  scrip_name: toString(
    row.scrip_name ?? row.scripName ?? row.SCRIP_NAME ?? row.stock_name ?? row.stockName,
  ),
  scrip_value: toNumber(row.scrip_value ?? row.scripValue ?? row.SCRIP_VALUE ?? row.stock_value),
  totalqty: toNumber(row.totalqty ?? row.TOTALQTY ?? row.total_qty ?? row.qty ?? row.QTY),
});

const extractRowsFromObject = (payload: unknown): EPledgeRawRecord[] => {
  if (!payload || typeof payload !== 'object') return [];
  const objectPayload = payload as EPledgeRawRecord;

  const candidateKeys = [
    'filtered_df',
    'data',
    'rows',
    'items',
    'list',
    'result',
    'holdings',
    'holdingData',
    'holding_data',
    'marginData',
    'margin_data',
  ];

  for (const key of candidateKeys) {
    const value = objectPayload[key];
    if (Array.isArray(value)) {
      return value.filter((item): item is EPledgeRawRecord => !!item && typeof item === 'object');
    }
  }

  const objectValues = Object.values(objectPayload);
  const nestedArray = objectValues.find((value) => Array.isArray(value));
  if (Array.isArray(nestedArray)) {
    return nestedArray.filter(
      (item): item is EPledgeRawRecord => !!item && typeof item === 'object',
    );
  }

  return [];
};

const normalizeResponseData = (response: unknown): EPledgeDataResponse['data'] => {
  if (!response || typeof response !== 'object') return [];

  const responseObject = response as EPledgeRawRecord;
  const topLevelData = responseObject.data;

  const rows = Array.isArray(topLevelData)
    ? topLevelData.filter((item): item is EPledgeRawRecord => !!item && typeof item === 'object')
    : extractRowsFromObject(topLevelData);

  return rows.map(normalizeRow).filter((row) => row.isin !== '' || row.scrip_name !== '');
};

export const ePledgeApi = createApi({
  reducerPath: 'ePledgeApi',
  baseQuery: createBaseQuery(E_PLEDGE_API_URL),
  tagTypes: ['EPledgeData'],
  endpoints: (builder) => ({
    getMarginEPledgeData: builder.query<EPledgeDataResponse, EPledgeDataParams>({
      query: ({ clientCode }) => ({
        url: '/v2/e-pledge/get-margin-epledge-data',
        method: 'GET',
        params: {
          clientCode,
          source: commonParams.source,
        },
      }),
      providesTags: (_result, _error, arg) => [{ type: 'EPledgeData', id: arg.clientCode }],
      transformResponse: (response: unknown) => {
        const responseObject = (response ?? {}) as EPledgeRawRecord;
        return {
          data: normalizeResponseData(responseObject),
          message: toString(responseObject.message),
          status: Boolean(responseObject.status),
          statusCode: toNumber(responseObject.statusCode),
        } as EPledgeDataResponse;
      },
      transformErrorResponse: createApiErrorTransformer('EPledgeData', false),
    }),
    submitMarginEPledge: builder.mutation<EPledgeSubmitResponse, EPledgeSubmitParams>({
      query: ({ body, clientCode }) => ({
        url: '/v2/e-pledge/margin-epledge',
        method: 'POST',
        params: {
          clientCode,
          source: commonParams.source,
        },
        body,
      }),
      invalidatesTags: ['EPledgeData'],
      transformErrorResponse: createApiErrorTransformer('EPledgeSubmit', false),
    }),
    getMarginMTFPledgeData: builder.query<EPledgeDataResponse, EPledgeDataParams>({
      query: ({ clientCode }) => ({
        url: '/v2/mtf-pledge/get-margin-mtf-pledge-data',
        method: 'GET',
        params: {
          clientCode,
          source: commonParams.source,
        },
      }),
      providesTags: (_result, _error, arg) => [{ type: 'EPledgeData', id: arg.clientCode }],
      transformResponse: (response: unknown) => {
        const responseObject = (response ?? {}) as EPledgeRawRecord;
        return {
          data: normalizeResponseData(responseObject),
          message: toString(responseObject.message),
          status: Boolean(responseObject.status),
          statusCode: toNumber(responseObject.statusCode),
        } as EPledgeDataResponse;
      },
      transformErrorResponse: createApiErrorTransformer('EPledgeData', false),
    }),
    submitMarginMTFPledge: builder.mutation<EPledgeSubmitResponse, EPledgeSubmitParams>({
      query: ({ body, clientCode }) => ({
        url: '/v2/mtf-pledge/margin-mtf-pledge',
        method: 'POST',
        params: {
          clientCode,
          source: commonParams.source,
        },
        body,
      }),
      invalidatesTags: ['EPledgeData'],
      transformErrorResponse: createApiErrorTransformer('EPledgeSubmit', false),
    }),
  }),
});

export const {
  useGetMarginEPledgeDataQuery,
  useSubmitMarginEPledgeMutation,
  useGetMarginMTFPledgeDataQuery,
  useSubmitMarginMTFPledgeMutation,
} = ePledgeApi;
